/**
 * Query-parameter state through Next's router — the one URL helper the editors share.
 *
 * WHY NOT `history.pushState`. Next stamps `__N: true` into every history entry it writes
 * (`node_modules/next/dist/shared/lib/router/router.js:1277-1286`) and its popstate handler
 * returns early on any entry without it (`if (!state.__N) { return; }`, `:481-483`). An entry
 * written directly therefore changes the URL and runs NO route logic when the user presses
 * Back, so the page keeps rendering what it was rendering while the address bar disagrees.
 * Everything here goes through `Router.push`/`Router.replace` with `shallow: true`, and
 * `utils/route-state.spec.ts` asserts both halves of that: the router mock was called, and
 * `window.history.pushState` was not.
 *
 * WHY IT VALIDATES NOTHING. `readTabParam` returns a raw string. Two editors live on these
 * pages and the owner's ruling is that they share leaf pieces and no coupling: there is no
 * shared `activeTab` union and no shared `readEditorTab`, and each page validates the string
 * against its own set (`STUDIO_PLAN_P2/progress.md:50,52`). A helper that narrowed the type
 * would be that coupling. The Studio's own reading is `components/studio/state/studio-url.ts`.
 *
 * WHY NO `popstate` REACHES A CALLER. STUDIO_DESIGN.md 6.4: "routeChangeComplete + pageshow
 * drive restore(); no popstate listener." Next already owns popstate for the entries it wrote
 * — it settles the URL and then fires `routeChangeComplete` — so a second listener that ran a
 * restore would run it twice for one Back. `onRouteChange` is therefore how Back and Forward
 * reach a caller, and the spec proves a Back restores the previous tab through it. The one
 * `popstate`/`pageshow` handler this module does bind (`forgetPending`) calls no subscriber
 * and reads no state: it only drops the in-flight intent, which a Back has just made stale
 * (review I2).
 *
 * `scroll: false` on every write: these are state changes within one page, and scrolling to
 * the top when a user opens an export sheet is a bug, not a navigation.
 *
 * NO REPO PRECEDENT. `grep -rn 'shallow: true'` over every `.ts`/`.tsx` outside node_modules
 * returned nothing when this was written, and so did `routeChangeComplete`. Ten files import
 * `Router from 'next/router'` and every one of them does a full navigation. Nothing else in
 * the repo will notice if this regresses, which is why the spec asserts the exact arguments.
 *
 * ── THE CONTRACT, for both editors that share this module ───────────────────────────────
 *
 * WRITES IN THE SAME TICK COMPOSE. Next writes history ASYNCHRONOUSLY: `change()` is `async`
 * (`shared/lib/router/router.js:770`) and only reaches `changeState` — the actual
 * `history.pushState` — after several awaits (`:1227`), even on the shallow path. A second
 * write in the same tick that re-read `window.location.search` would therefore read the
 * PRE-write URL and silently drop the first write's keys. So this module keeps the search
 * string it last ASKED for and seeds the next patch from it while a write is in flight
 * (`seedSearch`). `openStudioTab(id)` immediately followed by `pushExportStep()` therefore
 * lands one URL carrying `tab`, `sid` and `step`.
 *
 * Both writes COMPLETE — a shallow same-route write is not cancelled (`router.js:1353` returns
 * the existing route info before `:1356` installs the cancel handler, so the `clc` guard at
 * `:862` is false) — which is why the intent is released by comparing against the address bar
 * rather than on the first settle event to arrive. See `releasePending`.
 *
 * READS DO NOT. `readParam`/`readQuery`/`readTabParam` report the ADDRESS BAR, never the
 * in-flight intent — they are what a restore path and a `routeChangeComplete` subscriber
 * want. A caller that writes and then reads back in the same tick sees the old value; read
 * after `onRouteChange` fires, or keep the value you just wrote.
 *
 * A WRITE CAN BE A NO-OP. Before the router mounts, `Router.pathname` and `Router.push` both
 * throw `No router instance found` (`client/router.js:95-104,118-124`). There is no safe
 * fallback — `history.pushState` is the thing this module exists to avoid — so such a write
 * logs once at debug and LEAVES THE URL ALONE. Call these from a mounted component.
 *
 * `as` IS BUILT FROM `window.location.pathname` (plus the fragment), which already carries any
 * `basePath`. That is safe rather than double-prefixing: `change()` strips a basePath it recognises before
 * re-adding one (`router.js:868`, `hasBasePath(as) ? removeBasePath(as) : as`), and the same
 * line no-ops the locale when there is no `i18n` config. `next.config.js:95` is
 * `basePath: ''` today; this note is what keeps a future basePath from being a surprise.
 */
import Router from 'next/router';

/** A key set to `null` (or `''`) is removed; anything else is written. */
export type QueryPatch = Record<string, string | null>;

/** `[feature]`, `[...slug]`, `[[...slug]]` — the segments a shallow push has to carry. */
const DYNAMIC_SEGMENT = /\[\.{0,3}([^\]]+)\]/g;

function currentSearch(): string {
    if (typeof window === 'undefined') return '';
    return window.location.search || '';
}

// ------------------------------------------------------------ the in-flight write (I1, I2)

/**
 * The write this module last ASKED Next for. See "WRITES IN THE SAME TICK COMPOSE".
 *
 * The PATHNAME and the `as` are part of it, not just the search string (whole-branch review,
 * I2): an intent is a guess about ONE address. The moment the URL settles anywhere else — a
 * Back out of a shallow push, or any cross-route navigation, `Router.push('/downloads')`
 * included — a remembered search string describes a page that is no longer on screen, and
 * seeding the next write from it puts a resurrected `sid` on a foreign editor's page.
 */
interface PendingWrite {
    /** Without its leading `?`. */
    search: string;
    /** `window.location.pathname` as it was when the write was issued. */
    pathname: string;
    /** Exactly what Next was handed, so `routeChangeError` can be recognised as ours. */
    as: string;
}

let pending: PendingWrite | null = null;
let routerBound = false;
let windowBound = false;

function withoutQuestionMark(search: string): string {
    return search.charAt(0) === '?' ? search.slice(1) : search;
}

function currentPathname(): string {
    if (typeof window === 'undefined') return '';
    return window.location.pathname || '';
}

/** The address bar is showing exactly the URL this write asked for. */
function landed(write: PendingWrite): boolean {
    return currentPathname() === write.pathname && withoutQuestionMark(currentSearch()) === write.search;
}

/** The URL settled on a page this intent cannot describe at all. */
function leftThePage(write: PendingWrite): boolean {
    return currentPathname() !== write.pathname;
}

/**
 * Stop guessing — once the address bar shows what was asked for, OR once it has moved away.
 *
 * An unconditional release is wrong here, and the case is not hypothetical (review N1): Next
 * does NOT cancel a shallow same-route write, because `getRouteInfo` returns the existing
 * route info at `router.js:1353` BEFORE `getCancelledHandler` installs `clc` at `:1356`, so
 * the `_inFlightRoute && this.clc` guard at `:862` is false. Two same-tick writes therefore
 * both complete, in order — and write A's `routeChangeComplete` fires while write B is still
 * in flight. Clearing on that event would forget B, and a third write issued from the
 * subscriber that event just ran (the most likely place for one) would seed from A's URL and
 * drop B's keys. Comparing against the URL is the whole guard.
 *
 * The SECOND arm is I2's: a write that will never land must not be remembered for the life of
 * the SPA. Once `location.pathname` is not the intent's, the intent is unreachable by
 * construction — no shallow write from this module can move the URL back to it — so it
 * expires and the next write seeds from what the user can actually see.
 */
function releasePending(): void {
    if (pending === null) return;
    if (landed(pending) || leftThePage(pending)) pending = null;
}

/**
 * A navigation that ENDED IN AN ERROR, which is the other way one ends (`router.js:862-868`).
 *
 * Next passes the failed `as` as the second argument. When it is ours the write will never
 * reach the address bar, so the intent goes; when it is somebody else's the ordinary rule
 * applies. An event that names nothing releases too — an intent kept on a guess about an
 * event we could not read is the failure this arm exists to prevent (parked N4: the old
 * handler compared the bar to the intent, which an error can never make true).
 */
function releaseOnRouteError(_error?: unknown, as?: unknown): void {
    if (pending === null) return;
    if (typeof as !== 'string' || as === pending.as) {
        pending = null;
        return;
    }
    releasePending();
}

/**
 * Back, Forward and a bfcache restore move the address bar without this module asking, so
 * whatever it was guessing is stale by definition (review I2).
 *
 * This is NOT the `popstate` listener 6.4 forbids. That rule is about `restore()` running
 * twice for one Back; this handler reads no state, calls no subscriber and runs no restore —
 * it only FORGETS, so the next write seeds from the URL on screen. Next still owns the route
 * logic and still fires the `routeChangeComplete` that `onRouteChange` subscribers see.
 */
function forgetPending(): void {
    pending = null;
}

/**
 * Subscribe the release to every settle path, once.
 *
 * Two flags, not one: `Router.events` is a static property on the router class
 * (`client/router.js:88-89`) and is there from the first import, but a caller that somehow
 * reached a write before it existed must not be left with the window listeners bound and the
 * router ones silently skipped for the life of the page.
 */
function bindPendingRelease(): void {
    if (!windowBound && typeof window !== 'undefined') {
        window.addEventListener('popstate', forgetPending);
        window.addEventListener('pageshow', forgetPending);
        windowBound = true;
    }
    if (routerBound) return;
    const events = Router.events;
    if (!events) return;
    events.on('routeChangeComplete', releasePending);
    events.on('routeChangeError', releaseOnRouteError);
    routerBound = true;
}

/** What the next patch is applied to: the in-flight search if there is one, else the URL. */
function seedSearch(): string {
    if (pending === null) return currentSearch();
    if (landed(pending) || leftThePage(pending)) {
        // The address bar has caught up, or moved on; there is nothing left to remember.
        pending = null;
        return currentSearch();
    }
    return pending.search;
}

let loggedRouterUnavailable = false;

/**
 * A URL write that cannot happen is a debug line, not an exception and not a `pushState`.
 *
 * Once per page load: the failure is structural (the router has not mounted), so a caller
 * that retries would produce one line per keystroke.
 */
function noteRouterUnavailable(reason: unknown): void {
    if (loggedRouterUnavailable) return;
    loggedRouterUnavailable = true;
    // eslint-disable-next-line no-console
    console.debug('[route-state] no mounted Next router; URL left unchanged', reason);
}

/** One parameter from the live URL, or from `search` when the caller has it already. */
export function readParam(key: string, search?: string): string | null {
    const raw = search !== undefined ? search : currentSearch();
    if (!raw) return null;
    return new URLSearchParams(raw).get(key);
}

/** Several at once, absent keys reported as `null` so a caller can destructure. */
export function readQuery(keys: readonly string[], search?: string): Record<string, string | null> {
    const raw = search !== undefined ? search : currentSearch();
    const params = new URLSearchParams(raw || '');
    const out: Record<string, string | null> = {};
    for (const key of keys) out[key] = params.get(key);
    return out;
}

/** The `tab` parameter, as a raw string. Callers validate it against their own set. */
export function readTabParam(search?: string): string | null {
    return readParam('tab', search);
}

interface RouteBase {
    pathname: string;
    params: Record<string, string | string[]>;
}

/**
 * The route pattern and its dynamic segment values, so a shallow push keeps them.
 *
 * `pages/[feature].tsx` serves every converter family, so `Router.pathname` is `/[feature]`
 * while the visible path is `/convert-image-to-video`. A push that passed only the visible
 * path would leave Next unable to match the route and would fall back to a full navigation —
 * which is the standard way to lose a shallow push on a dynamic route.
 *
 * `null` rather than a throw when the singleton has no router yet: `next/router`'s
 * `pathname`/`query` getters throw "No router instance found" until the router mounts
 * (`node_modules/next/dist/client/router.js:95-104`).
 *
 * `null` means THE WRITE DOES NOT HAPPEN — it is not a fallback. `push`/`replace` are defined
 * on the same singleton as `(...args) => getRouter()[field](...args)` (`:118-124`), so the
 * very state that makes this return `null` makes the write throw a line later; and the only
 * pathname available here would be the visible path, which already carries the query string
 * and would format as `/convert-image-to-video?tab=studio?tab=studio`. (`Router.events` is
 * exempt from all of this — it is a static property on the router class, which
 * `client/router.js:88-89` says in as many words.)
 */
function routeBase(): RouteBase | null {
    try {
        const pathname = Router.pathname;
        if (typeof pathname !== 'string' || pathname.length === 0) return null;
        const query = Router.query || {};
        const params: Record<string, string | string[]> = {};
        for (const match of pathname.matchAll(DYNAMIC_SEGMENT)) {
            const key = match[1];
            const value = query[key];
            if (value !== undefined) params[key] = value;
        }
        return { pathname, params };
    } catch (e) {
        noteRouterUnavailable(e);
        return null;
    }
}

/**
 * A rejected route change is Next's business, not the caller's.
 *
 * `Router.push` returns a promise that rejects when a navigation is cancelled; leaving it
 * unhandled would surface as an unhandled rejection in a page that did nothing wrong.
 */
function swallow(result: unknown): void {
    if (result && typeof (result as PromiseLike<unknown>).then === 'function') {
        void Promise.resolve(result as PromiseLike<unknown>).catch(() => undefined);
    }
}

function applyQuery(patch: QueryPatch, method: 'push' | 'replace'): void {
    if (typeof window === 'undefined') return;
    // Seeded from the in-flight search, not the address bar, so two writes in one tick
    // compose instead of the second dropping the first (see the header's contract).
    const params = new URLSearchParams(seedSearch());
    for (const key of Object.keys(patch)) {
        const value = patch[key];
        if (value === null || value === '') params.delete(key);
        else params.set(key, value);
    }
    const search = params.toString();
    const base = routeBase();
    // No mounted router means no write at all: `Router.push` would throw a line below, and
    // there is no honest fallback that is not `history.pushState`.
    if (!base) return;
    // A bare path stays bare: a trailing `?` is a different URL to a crawler and to a diff.
    // The FRAGMENT is carried through: it is part of the address the user is on, and an `as`
    // that dropped it would scroll an anchored page back to the top on the next state change
    // (parked N4's sibling — `window.location.hash` is '' or already carries its own '#').
    const pathname = window.location.pathname;
    const as = `${pathname}${search ? `?${search}` : ''}${window.location.hash || ''}`;
    const query: Record<string, string | string[]> = { ...base.params };
    params.forEach((value, key) => {
        query[key] = value;
    });
    const url = { pathname: base.pathname, query };
    const options = { shallow: true, scroll: false };
    bindPendingRelease();
    try {
        swallow(method === 'push' ? Router.push(url, as, options) : Router.replace(url, as, options));
    } catch (e) {
        // The router can throw between the two reads above and this call. Leave the URL alone.
        noteRouterUnavailable(e);
        return;
    }
    // Only after the call returns, for two reasons that both survive N1's correction. A write
    // that throws must not be remembered as in flight (that is what the `catch` above proves);
    // and Next runs everything up to its cancellation block synchronously inside `push` for a
    // shallow write (the one `await` before `router.js:862` is at `:783`, guarded by
    // `!options.shallow`), so if a peer ever issues a NON-shallow write that does emit
    // `routeChangeError`, that emit lands before this line rather than wiping it.
    pending = { search, pathname, as };
}

/** A new history entry — Back returns the user to where they were. */
export function pushQuery(patch: QueryPatch): void {
    applyQuery(patch, 'push');
}

/** No new history entry — for a state the user should not have to press Back through. */
export function replaceQuery(patch: QueryPatch): void {
    applyQuery(patch, 'replace');
}

/** `pushQuery` on the one key `tab`. `null` removes it. */
export function writeTabParam(tab: string | null, options?: { replace?: boolean }): void {
    if (options?.replace) replaceQuery({ tab });
    else pushQuery({ tab });
}

/**
 * Run `fn` whenever the URL settles — a shallow push, a Back, a Forward, or a restore from
 * the back/forward cache.
 *
 * `pageshow` is the bfcache case, which fires no route event. Never rely on bfcache for
 * correctness: this exists so a bfcache restore is not WORSE than a reload, not so it can be
 * counted on.
 */
export function onRouteChange(fn: () => void): () => void {
    if (typeof window === 'undefined') return () => undefined;
    const handler = (): void => fn();
    const events = Router.events;
    if (events) events.on('routeChangeComplete', handler);
    window.addEventListener('pageshow', handler);
    return () => {
        if (events) events.off('routeChangeComplete', handler);
        window.removeEventListener('pageshow', handler);
    };
}
